/**
 * Toucan Music's shell. It draws the header with the hash-routed page below
 * it, and the dialogs that belong to no page (the device picker, Manage
 * shelves and New shelf).
 *
 * It also holds the state that crosses pages and stays out of the synced
 * store: what Spotify is playing here and elsewhere, which device was picked,
 * and how the library grid is sorted and filtered.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";
import {
  leaveFor,
  listById,
  required,
  styled,
  takenNames,
  ToucanAppMenu,
  ToucanNewThingDialog,
  ToucanPageHeading,
  ToucanSettings,
  useRoute,
  useStore,
} from "toucan";
import {
  PlayingIndicator,
  StartingIndicator,
} from "./components/PlayingIndicator.tsx";
import { DeviceDialog } from "./components/DeviceDialog.tsx";
import { ShelvesDialog } from "./components/ShelvesDialog.tsx";
import { SpotifySettings } from "./components/SpotifySettings.tsx";
import { AlbumPage } from "./pages/AlbumPage.tsx";
import { SearchPage } from "./pages/SearchPage.tsx";
import { ConnectPage } from "./pages/ConnectPage.tsx";
import { ImportPage } from "./pages/ImportPage.tsx";
import { LibraryPage } from "./pages/LibraryPage.tsx";
import { connectSpotify, disconnectSpotify } from "./connect.tsx";
import {
  IN_PAGE_DEVICE,
  chosenTarget,
  fetchDevices,
  moveToDevice,
  needsDevices,
  playTarget,
  preferredDevice,
} from "./devices.ts";
import type { PlayTarget } from "./devices.ts";
import { importTitle } from "./import.ts";
import { rememberSort, storedSort } from "./library.ts";
import {
  addShelf,
  albumById,
  RESERVED_SHELF_NAMES,
  setAlbumShelf,
} from "./model.ts";
import type { AlbumSort } from "./model.ts";
import type { PlaybackState } from "./player.ts";
import {
  albumPlaying,
  nowPlaying,
  runTransport,
  seekPlayback,
  setPlaybackVolume,
  startAlbum,
} from "./playback.ts";
import type { TransportCommand } from "./playback.ts";
import { currentRoute, go, libraryPath, noteLibraryView } from "./routes.ts";
import type { Route } from "./routes.ts";
import { accountPlayback, isConnected } from "./spotify.ts";
import type { AccountPlayback, Device } from "./spotify.ts";
import { app, store } from "./state.ts";
import { pageTitle } from "./title.ts";
import type { JSX } from "preact";

/**
 * How often the account is asked what it is playing elsewhere. It is asked
 * often while a device plays, since the album page's rail shows that reading,
 * and rarely otherwise.
 */
const PLAYING_POLL_MS = 5000;
const IDLE_POLL_MS = 30_000;

/**
 * How long after a command Spotify is asked what came of it. A device answers
 * a command well before the next poll is due, and the button that flipped on
 * press is only confirmed by that answer.
 */
const CONFIRM_MS = 300;

/** How long a press waits to be told what came of it before giving up on it. */
const GIVE_UP_MS = 10_000;

const Bar = styled("header")`
  /* What is playing and where it comes out, together at the end of the row so
     the page's own title keeps the start of it. */
  .controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    margin-left: auto;
  }

  /* Where the audio comes out, always visible without opening the picker. */
  .device {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin: 0;
    padding: 0.3rem 0.75rem;
    border: 1px solid var(--toucan-border-strong);
    border-radius: 999px;
    background: var(--toucan-surface);
    color: var(--toucan-ink);
    font-size: 1rem;
    white-space: nowrap;
    cursor: pointer;
  }

  /* On iOS a glass capsule (\`.toucan-glass\` in theme.css). */
  :root[data-platform="ios"] & .device {
    min-height: 44px;
    border-color: transparent;
    background: none;
  }

  /* Says the pill opens something. Pico's <summary> icon, so this pill and
     the brand menu beside it have the same mark. */
  .chevron {
    width: 0.9rem;
    height: 0.9rem;
    background-image: var(--pico-icon-chevron);
    background-position: center;
    background-size: 0.9rem auto;
    background-repeat: no-repeat;
  }

  .target {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* What the player is doing while it is still coming up. */
  .status {
    margin: 0;
    color: var(--toucan-muted);
    white-space: nowrap;
  }

  /* On a phone the page title and the pill both need the width, so the pill
     drops its lead-in and keeps only the device's name. */
  @media (max-width: 480px) {
    .prefix {
      display: none;
    }

    .target {
      max-width: 7rem;
    }

    /* A phone drops the album's name, leaving the playing bars next to the
       pill. A wider gap than the one between sleeve and bars keeps them
       reading as part of what is playing rather than part of the pill. */
    .controls {
      gap: 1rem;
    }
  }
`;

/** The pages the shell draws in `main`. */
type MainPage = "library" | "album" | "search" | "import" | "settings";

/**
 * The section each route shows. A shelf filter is the library page with a chip
 * pressed, not a page of its own.
 */
const SECTIONS: Record<Route["name"], MainPage> = {
  library: "library",
  shelf: "library",
  unshelved: "library",
  shelves: "library",
  album: "album",
  search: "search",
  import: "import",
  settings: "settings",
};

/** The route to show, which is the library while a stale route redirects. */
function shownRoute(route: Route, stale: boolean): Route {
  return stale ? { name: "library" } : route;
}

/**
 * One key per page that is scrolled separately. A shelf and the whole library
 * are different lists and keep their own places; an import's two screens are
 * one page taken in two steps, so they share.
 */
function scrollKey(route: Route): string {
  return route.name === "shelf" || route.name === "album"
    ? `${route.name}/${route.id}`
    : route.name;
}

/** The brand row, with the app's icon and name and the dropdown menu. */
function BrandMenu({ onShelves }: { onShelves: () => void }) {
  return (
    <ToucanAppMenu app={app()} label="Music" icon="icon.svg">
      <li>
        <button
          type="button"
          onClick={() => {
            onShelves();
          }}
        >
          Manage shelves
        </button>
      </li>
      <li>
        <button
          type="button"
          onClick={() => {
            go("import");
          }}
        >
          Import albums
        </button>
      </li>
    </ToucanAppMenu>
  );
}

export function Shell() {
  const route = useRoute(currentRoute);
  useStore(app());
  const [connected, setConnected] = useState(isConnected);
  const [playback, setPlayback] = useState<PlaybackState | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [remote, setRemote] = useState<AccountPlayback | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  /**
   * The album a press of Play asked for before there was anywhere to play it,
   * waiting on the device picker that press opened. Cleared by the pick, and
   * by closing the picker without making one.
   */
  const [queued, setQueued] = useState<string | null>(null);
  /** The name a "＋ New shelf" chip suggests, while that dialog is open. */
  const [newShelf, setNewShelf] = useState<{ suggested: string } | null>(null);
  const [createdShelfId, setCreatedShelfId] = useState<string | null>(null);
  /**
   * The record the search has just added, whose page opens with the note field
   * ready. Held here because the search page sets it and the album page reads
   * it.
   */
  const [noting, setNoting] = useState<string | null>(null);
  // Which way this browser likes the grid: the order last chosen here.
  const [sort, setSort] = useState<AlbumSort>(storedSort);
  const [filter, setFilter] = useState("");
  /**
   * The album a press of Play asked for, until Spotify reports it playing.
   * Held as what the header has to draw rather than as an id, so the record
   * being started is never one the store has to be asked for again.
   */
  const [starting, setStarting] = useState<{
    id: string;
    name: string;
    image: string | undefined;
    /** What was playing when Play was pressed, which is what has to change. */
    wasUri: string | null;
  } | null>(null);
  const [hidden, setHidden] = useState(document.hidden);

  /**
   * How far each page had been scrolled, keyed by `scrollKey`, so going back
   * to a list scrolls to the record you opened. A page not seen before starts
   * at the top. Kept for this visit only.
   */
  const main = useRef<HTMLElement>(null);
  const scrolled = useRef(new Map<string, number>());
  const at = useRef(scrollKey(route));

  const target = playTarget(deviceId, devices);
  const reports = { onState: setPlayback, onConnecting: setConnecting };

  // An album that has gone (removed here, or synced away) redirects to the
  // library view it was opened from. The back arrow and a removal from the
  // page both lead there, which matters because both run for one removal.
  const album =
    route.name === "album" ? albumById(store(), route.id) : undefined;
  const stale = route.name === "album" && album === undefined;
  useEffect(() => {
    if (stale) {
      go(libraryPath());
    }
  }, [stale]);
  // A shelf that has gone (a stale link, or deleted on another device)
  // redirects to the whole library.
  const staleShelf =
    route.name === "shelf" && listById(store().shelves, route.id) === undefined;
  useEffect(() => {
    if (staleShelf) {
      go("");
    }
  }, [staleShelf]);
  const shown = shownRoute(route, stale || staleShelf);
  useEffect(() => {
    // Record the route itself, not the whole-library stand-in `shownRoute`
    // uses while a removed album's page redirects, which would forget the
    // shelf. A deleted shelf is not recorded.
    if (!staleShelf) {
      noteLibraryView(route);
    }
  }, [route, staleShelf]);
  // Run on every render, not only on a route change, since a title can hold
  // an album's or a shelf's name and a rename has to reach the tab.
  useEffect(() => {
    document.title = pageTitle(shown);
  });
  // A layout effect, so the key moves before the browser can report a scroll.
  // A shorter new page clamps the scroll position, and that clamp would
  // otherwise be recorded against the page being left.
  useLayoutEffect(() => {
    at.current = scrollKey(shownRoute(route, stale));
    required(main.current, "main").scrollTop =
      scrolled.current.get(at.current) ?? 0;
  }, [route, stale]);
  /** Which source an import is being made from, or null off the import page. */
  const source = shown.name === "import" ? shown.source : null;

  const refreshDevices = useCallback(() => {
    void (async () => {
      const found = await fetchDevices();
      if (found !== null) {
        setDevices(found);
      }
    })();
  }, []);

  // Only worth asking Spotify for the open devices when the answer decides
  // where this browser would play; otherwise every load costs a network round
  // trip nothing here needs.
  useEffect(() => {
    if (!connected || !needsDevices(navigator.userAgent)) {
      return;
    }
    void (async () => {
      const found = await fetchDevices();
      if (found !== null) {
        setDevices(found);
        setDeviceId(preferredDevice(found));
      }
    })();
  }, [connected]);

  /**
   * Ask Spotify what the account is playing elsewhere. Silent about failure,
   * since it runs on a clock and a flaky network would toast every few
   * seconds.
   *
   * The clock stops while this page is the player: the SDK reports all the
   * header and album page show, except the track number (see `trackHere`).
   */
  const poll = (): void => {
    if (!connected) {
      return;
    }
    void (async () => {
      try {
        setRemote(await accountPlayback());
      } catch {
        setRemote(null);
      }
    })();
  };
  // The clock outlives the render that set it, so the check it makes reaches
  // the current one through a ref.
  const asking = useRef(poll);
  asking.current = poll;

  // A hidden tab stops polling until it is shown again.
  useEffect(() => {
    const watch = (): void => {
      setHidden(document.hidden);
    };
    addEventListener("visibilitychange", watch);
    return () => {
      removeEventListener("visibilitychange", watch);
    };
  }, []);

  // A pending play polls at the playing rate. IDLE_POLL_MS is longer than
  // GIVE_UP_MS, so a play started from silence would otherwise give up before
  // a poll could confirm it.
  const cadence =
    remote?.is_playing === true || starting !== null
      ? PLAYING_POLL_MS
      : IDLE_POLL_MS;
  // A flag rather than the state itself, which is a new object on every
  // player report and would rebuild the clock each time.
  const playingHere = playback !== null;
  useEffect(() => {
    if (hidden || playingHere) {
      return;
    }
    asking.current();
    const timer = setInterval(() => {
      asking.current();
    }, cadence);
    return () => {
      clearInterval(timer);
    };
  }, [hidden, cadence, playingHere]);

  // A track's number in its album is the one thing the SDK never says, so a
  // track starting in this tab costs one question rather than a clock of them.
  const trackHere = playback?.track_window.current_track.name ?? null;
  useEffect(() => {
    if (trackHere !== null) {
      asking.current();
    }
  }, [trackHere]);

  /** The confirming poll a command asks for, replaced by the next command. */
  const confirming = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(
    () => () => {
      clearTimeout(confirming.current);
    },
    [],
  );

  /** What is playing: this tab's player if it has a state, else the account. */
  const playing = nowPlaying(playback, remote);

  /**
   * The optimistic state lasts until Spotify reports a different record from
   * the one playing when Play was pressed, so pressing Play on a second album
   * while a first plays still waits.
   */
  const arrived =
    starting !== null && (playing?.uri ?? null) !== starting.wasUri;
  useEffect(() => {
    if (arrived) {
      setStarting(null);
    }
  }, [arrived]);
  const awaited = arrived ? null : starting;

  // A play Spotify accepted but never reports would otherwise leave the button
  // saying "Starting…" for ever, with no way to press it again.
  useEffect(() => {
    if (starting === null) {
      return;
    }
    const timer = setTimeout(() => {
      setStarting(null);
    }, GIVE_UP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [starting]);

  /** Work the transport, then ask Spotify what came of it. */
  const command = (name: TransportCommand): void => {
    void runTransport(name, required(playing, "playing"));
    clearTimeout(confirming.current);
    confirming.current = setTimeout(() => {
      asking.current();
    }, CONFIRM_MS);
  };

  const moveThroughTrack = (position: number): void => {
    void seekPlayback(position, required(playing, "playing"));
  };

  const changeVolume = (percent: number): void => {
    void setPlaybackVolume(percent, required(playing, "playing"));
  };

  /** Start an album on a device, saying so before Spotify answers. */
  const startOn = (albumId: string, where: PlayTarget): void => {
    // The screen says what was asked for straight away; what it says next is
    // Spotify's answer, whether that is the record playing or a refusal.
    const asked = required(albumById(store(), albumId), "album");
    setStarting({
      id: asked.id,
      name: asked.name,
      image: asked.image,
      wasUri: playing?.uri ?? null,
    });
    void (async () => {
      if (await startAlbum(albumId, where, reports)) {
        // Spotify has taken it; ask what came of it rather than waiting out
        // the interval, as a transport command does.
        clearTimeout(confirming.current);
        confirming.current = setTimeout(() => {
          asking.current();
        }, CONFIRM_MS);
      } else {
        setStarting(null);
      }
    })();
  };

  /**
   * Play an album, or, on a browser that can only play through a Connect
   * device and has none open, open the picker and play it on the device
   * chosen there.
   */
  const play = (albumId: string): void => {
    if (target.kind === "none") {
      setQueued(albumId);
      setPicking(true);
      return;
    }
    startOn(albumId, target);
  };

  const openNewShelf = (suggested: string): void => {
    setNewShelf({ suggested });
  };

  const closeNewShelf = useCallback(() => {
    setNewShelf(null);
  }, []);

  /** The album page has opened its note field, so the instruction is spent. */
  const noteOpened = useCallback(() => {
    setNoting(null);
  }, []);

  const shelfCreated = useCallback(
    (name: string, emoji: string) => {
      const created =
        emoji === "" ? addShelf(store(), name) : addShelf(store(), name, emoji);
      // A shelf made from the album page files that album on it.
      if (route.name === "album") {
        setAlbumShelf(
          required(albumById(store(), route.id), "album"),
          created.id,
        );
      }
      // A shelf made from an import becomes the shelf the import adds to.
      if (route.name === "import") {
        setCreatedShelfId(created.id);
      }
    },
    [route],
  );

  const pages: Record<MainPage, () => JSX.Element> = {
    library: () => (
      <LibraryPage
        route={shown}
        sort={sort}
        onSort={(next) => {
          setSort(next);
          rememberSort(next);
        }}
        filter={filter}
        onFilter={setFilter}
        playing={playing}
      />
    ),
    album: () => {
      const showing = required(album, "album");
      return (
        <AlbumPage
          key={showing.id}
          album={showing}
          playing={playing}
          starting={awaited?.id === showing.id}
          onPlay={() => {
            play(showing.id);
          }}
          onCommand={command}
          onSeek={moveThroughTrack}
          onVolume={changeVolume}
          onNewShelf={() => {
            openNewShelf("");
          }}
          writingNote={noting === showing.id}
          onNoteOpened={noteOpened}
        />
      );
    },
    search: () => <SearchPage onAdded={setNoting} />,
    settings: () => (
      <ToucanSettings app={app()}>
        <SpotifySettings
          connected={connected}
          onConnect={() => {
            void connectSpotify(leaveFor);
          }}
          onDisconnect={() => {
            disconnectSpotify();
            setConnected(false);
            setPlayback(null);
            // The devices and the chosen one belong to the account that has
            // just gone; keeping them would leave the header naming a speaker
            // nothing may play to.
            setDevices([]);
            setDeviceId(null);
          }}
        />
      </ToucanSettings>
    ),
    import: () => (
      // Each source gets a fresh screen, which drops the last one's review.
      <ImportPage
        key={source ?? "chooser"}
        source={source}
        createdShelfId={createdShelfId}
        onNewShelf={openNewShelf}
        onAdded={(shelfId) => {
          // Land on what the import just filled, rather than on an emptied
          // review.
          go(shelfId === null ? "" : `shelf/${shelfId}`);
        }}
      />
    ),
  };

  /** The page a route shows. */
  const showing = SECTIONS[shown.name];
  // Every page but Settings needs Spotify, so a browser with no tokens gets
  // the connect page whatever the hash says. Settings is where it connects.
  const open = connected || showing === "settings";
  const atHome = !open || showing === "library";

  /**
   * The back arrow. A source screen goes back to the chooser it was opened
   * from, and every other page to the library view it was opened from, shelf
   * filter and all.
   */
  const back = (): void => {
    go(
      shown.name === "import" && shown.source !== null
        ? "import"
        : libraryPath(),
    );
  };

  /**
   * The header's playing status. It says the player is starting, and then
   * shows the record itself for as long as there is one.
   */
  const indicator = connecting ? (
    <p class="status">Starting the player…</p>
  ) : (
    <>
      {/* On the record's own page the button already says it is starting, so
          the header does not say it twice — the same rule the indicator
          follows once the music is on. */}
      {awaited !== null &&
        !(shown.name === "album" && shown.id === awaited.id) && (
          <StartingIndicator album={awaited} />
        )}
      {awaited === null && playing !== null && (
        <PlayingIndicator
          playing={playing}
          onPage={shown.name === "album" && albumPlaying(playing, shown.id)}
          onShowAlbum={(albumId) => {
            go(`album/${albumId}`);
          }}
        />
      )}
    </>
  );

  return (
    <>
      <Bar>
        {atHome ? (
          <BrandMenu
            onShelves={() => {
              go("shelves");
            }}
          />
        ) : (
          <>
            {/* An album page's header names the artist, not the record: the
                record's own name is under its sleeve, and the header is the
                one slot on the screen that would otherwise repeat it. */}
            <ToucanPageHeading onBack={back}>
              {showing === "album" && required(album, "album").artist}
              {showing === "search" && "Add an album"}
              {showing === "import" && importTitle(source)}
              {showing === "settings" && "Settings"}
            </ToucanPageHeading>
          </>
        )}
        <div class="controls">
          {/* No indicator on an import: tapping it would leave the page and
              lose the review, which is held in local state. */}
          {showing !== "import" && indicator}
          {/* Nothing can be chosen before the account is connected — and asking
              Spotify for devices without a token only raises a failure to
              apologise for — so until then there is no button. */}
          {connected && (
            <button
              type="button"
              class="device toucan-glass"
              onClick={() => {
                setPicking(true);
              }}
            >
              <span class="prefix">Play on:</span>
              <span class="target">{target.name}</span>
              <span class="chevron" aria-hidden="true" />
            </button>
          )}
        </div>
      </Bar>
      <main
        ref={main}
        onScroll={(event) => {
          scrolled.current.set(at.current, event.currentTarget.scrollTop);
        }}
      >
        {open ? (
          pages[showing]()
        ) : (
          <ConnectPage
            onConnect={() => {
              void connectSpotify(leaveFor);
            }}
          />
        )}
      </main>
      {picking && (
        <DeviceDialog
          devices={devices}
          // The picker marks where the music would go, which on a browser
          // with nothing chosen is whatever the fallback settled on. Otherwise
          // the header names a phone the picker shows unpicked.
          chosen={target.kind === "device" ? target.id : null}
          onChoose={(row) => {
            const chosen = row === IN_PAGE_DEVICE ? null : row;
            const where = chosenTarget(chosen, devices);
            setDeviceId(chosen);
            setPicking(false);
            setQueued(null);
            // An album waiting on this answer is what the device was chosen
            // for, so it starts there. Moving what was playing across instead
            // would be moving it out of the way of the record asked for.
            if (queued !== null) {
              startOn(queued, where);
              return;
            }
            void moveToDevice(chosen, where.name, playback);
          }}
          onRefresh={refreshDevices}
          onClose={() => {
            setPicking(false);
            setQueued(null);
          }}
        />
      )}
      {shown.name === "shelves" && (
        <ShelvesDialog
          onClose={() => {
            go(libraryPath());
          }}
        />
      )}
      {newShelf !== null && (
        <ToucanNewThingDialog
          thing="shelf"
          placeholder="Late nights, Piano, Something loud…"
          initialName={newShelf.suggested}
          reserved={RESERVED_SHELF_NAMES}
          taken={takenNames(store().shelves)}
          onSave={shelfCreated}
          onClose={closeNewShelf}
        />
      )}
    </>
  );
}
